<template>
  <div class="hidden md:flex gap-2 flex-wrap">
    <filter-btn v-if="company" :text="`Empresa: ${company}`" :shouldRemove="false" />
    <template v-if="healthUnits?.length">
      <filter-btn
        v-for="healthUnit of healthUnits"
        :key="healthUnit"
        :text="`Unidade: ${healthUnit}`"
        :shouldRemove="false"
      />
    </template>
    <template v-if="schedules?.length">
      <filter-btn
        v-for="schedule of schedules"
        :key="schedule"
        :text="`Escala: ${schedule}`"
        :shouldRemove="false"
      />
    </template>
    <template v-if="shifts?.length">
      <filter-btn
        v-for="shift of shifts"
        :key="shift"
        :text="`Turno: ${shift}`"
        :shouldRemove="false"
      />
    </template>
    <template v-if="positions?.length">
      <filter-btn
        v-for="position of positions"
        :key="position"
        :text="`Posição: ${position}`"
        :shouldRemove="false"
      />
    </template>
    <template v-if="doctors?.length">
      <filter-btn
        v-for="doctor of doctors"
        :key="doctor"
        :text="`Dr(a): ${doctor}`"
        :shouldRemove="false"
      />
    </template>
    <filter-btn
      v-if="filters.valueMin"
      :text="`Valor Min.: R$ ${filters.valueMin}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.valueMax"
      :text="`Valor Max.: R$ ${filters.valueMax}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.paymentValueMin"
      :text="`Valor Aprov Min.: R$ ${filters.paymentValueMin}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.paymentValueMax"
      :text="`Valor Aprov Max.: R$ ${filters.paymentValueMax}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.startDate"
      :text="`Data Ini.: ${formatDate(filters.startDate)}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.endDate"
      :text="`Data Fin.: ${formatDate(filters.endDate)}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.weekDays?.length"
      :text="`Dias: ${filters.weekDays.map((d) => daysWeekObj[d]).join(', ')}`"
      :shouldRemove="false"
    />
    <filter-btn v-if="weekOfMonth" :text="`Semana: ${weekOfMonth}`" :shouldRemove="false" />
    <filter-btn
      v-if="stationWeekParity"
      :text="`Paridade: ${stationWeekParity}`"
      :shouldRemove="false"
    />
    <filter-btn v-if="isOpenDoctorStation" :text="isOpenDoctorStation" :shouldRemove="false" />
    <filter-btn
      v-if="coverageStatusLabel"
      :text="coverageStatusLabel"
      :shouldRemove="false"
      data-testid="consult-coverage-status-chip"
    />
    <filter-btn
      v-if="filters.doctorName"
      :text="`Médico: ${filters.doctorName}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="isDashboardV2 && filters.sectorIds?.length"
      :text="`Setor(es): ${sectorNames}`"
      :shouldRemove="false"
      data-testid="consult-filter-sector-chip"
    />
    <filter-btn
      v-if="isDashboardV2 && specialtyNames"
      :text="`Especialidade(s): ${specialtyNames}`"
      :shouldRemove="false"
      data-testid="consult-filter-specialty-chip"
    />
    <filter-btn
      v-if="filters.doctorStatus !== undefined && filters.doctorStatus !== null"
      :text="`Dr. Confirmou: ${filters.doctorStatus ? 'Sim' : 'Não'}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.crms?.length"
      :text="`CRM: ${filters.crms.join(', ')}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.crmStates?.length"
      :text="`UF CRM: ${filters.crmStates.join(', ')}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.hasException !== undefined && filters.hasException !== null"
      :text="`Excepcionalidade: ${filters.hasException ? 'Sim' : 'Não'}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.startHour?.length"
      :text="`Hora Início: ${filters.startHour.join(', ')}`"
      :shouldRemove="false"
    />
    <filter-btn
      v-if="filters.endHour?.length"
      :text="`Hora Fim: ${filters.endHour.join(', ')}`"
      :shouldRemove="false"
    />
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import FilterBtn from '@/components/Dashboard/Header/components/FilterBtn.vue'
import { useFilterStationsStore } from '@/components/Filters/useFilterStation'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { computed } from 'vue'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { usePositionStore } from '@/pinia-store/usePositionStore'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import { format } from 'date-fns'
import { daysWeekObj } from '@/utils/date'
import { useSectorStore } from '@/pinia-store/useSectorStore'
import { useSpecialtyStore } from '@/pinia-store/useSpecialtyStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { formatCoverageStatusFilterLabel } from '@/utils/coverageFilters'

const useFilterStations = useFilterStationsStore()
const { filters } = storeToRefs(useFilterStations)

const { listCompanies } = storeToRefs(useCompanyStore())
const { listHealthUnits } = storeToRefs(useHealthUnitStore())
const { listSchedules } = storeToRefs(useScheduleStore())
const { listPositions } = storeToRefs(usePositionStore())
const { listDoctorsCompany } = storeToRefs(useDoctorStore())
const { listShifts } = storeToRefs(useShiftStore()) // Refs dos Turnos

// A11: chips de setor/especialidade só com a flag ligada (na base o de setor era TODO).
const isDashboardV2 = useFeatureFlags().flags.enabledDashboardV2
const sectorStore = useSectorStore()
const specialtyStore = useSpecialtyStore()
// Sem o catálogo carregado (deep link), o chip mostra `#id` em vez de sumir.
const sectorNames = computed(() =>
  (filters.value.sectorIds ?? [])
    .map((id) => sectorStore.sectorName(Number(id)) ?? `#${id}`)
    .join(', ')
)
const specialtyNames = computed(() =>
  [
    ...(filters.value.specialtyIds ?? []).map(
      (id) => specialtyStore.specialtyName(`o:${id}`) ?? `#${id}`
    ),
    ...(filters.value.companySpecialtyIds ?? []).map(
      (id) => specialtyStore.specialtyName(`c:${id}`) ?? `#${id}`
    )
  ].join(', ')
)

const company = computed(() => {
  return listCompanies.value.find((c) => +c.value === Number(filters.value.company))?.title
})

const healthUnits = computed(() => {
  const healthUnits = listHealthUnits.value.filter((h) =>
    filters.value.healthUnits?.map((hu) => Number(hu))?.includes(+h.value)
  )
  return healthUnits.map((h) => h.title)
})

const schedules = computed(() => {
  const schedules = listSchedules.value.filter((h) =>
    filters.value.schedules?.map((hu) => Number(hu))?.includes(+h.value)
  )
  return schedules.map((h) => h.title)
})

const shifts = computed(() => {
  const shifts = listShifts.value.filter((s) =>
    filters.value.shifts?.map((id) => Number(id))?.includes(+s.id)
  )
  return shifts.map((s) => s.name)
})

const positions = computed(() => {
  const positions = listPositions.value.filter((h) =>
    filters.value.positions?.map((hu) => Number(hu))?.includes(+h.id)
  )
  return positions.map((h) => h.name)
})

const doctors = computed(() => {
  const doctors = listDoctorsCompany.value.filter((h) =>
    filters.value.doctors?.map((hu) => Number(hu))?.includes(+h.value)
  )
  return doctors.map((h) => h.title)
})

const weekOfMonth = computed(() => {
  if (!filters.value.weekOfMonth?.length) return null
  const labels = ['A', 'B', 'C', 'D', 'E']
  return filters.value.weekOfMonth
    .map((w) => labels[Number(w)])
    .filter(Boolean)
    .join(', ')
})

const stationWeekParity = computed(() => {
  const val = filters.value.stationWeekParity
  if (val === undefined || val === null) return null
  return Number(val) === 0 ? 'Par' : 'Ímpar'
})

const coverageStatusLabel = computed(() =>
  formatCoverageStatusFilterLabel(filters.value.coverageStatus ?? [])
)

const isOpenDoctorStation = computed(() => {
  const val = filters.value.isOpenDoctorStation
  if (val === true) return 'Apenas Vagas Abertas'
  if (val === false) return 'Apenas Vagas Preenchidas'
  return null
})

function formatDate(date: string | Date) {
  if (typeof date !== 'string') {
    return format(date, 'dd/MM/yyyy')
  }
  return date.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1')
}
</script>
